import { Flex, Stack, Text } from "@chakra-ui/react";
import { Option } from "@intentsify/types";
import { useQuery } from "@tanstack/react-query";
import { Endpoints } from "api";
import {
  Button,
  DayPicker,
  FILTER_LABEL_TEXT_PROPS,
  Filter,
  Search,
  Select,
  Table,
  ViewContainer,
} from "components";
import { PaginationType } from "components/Table/Table/Table.types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiDownload } from "react-icons/fi";
import { useComponentColors } from "theme";
import { FetchDataParamsWithToken } from "types/FetchDataParams";
import { useDownloadFile, useScreen } from "utils";
import {
  UserActivityLogTableRow,
  getUserAuditLogColumns,
} from "./UserAuditLog.columns";
import { UserAuditLogDetailsDrawer } from "./UserAuditLog.details";
import { UserAuditLogScreenDefinition } from "./UserAuditLog.definition";
import {
  isUserAuditLogFiltersEmpty,
  useUserAuditLogFilters,
} from "./UserAuditLog.filters";
import {
  UserAuditLogOrderBy,
  buildUserAuditLogExportParams,
  defaultListUserActivityLogsParams,
  listUserActivityLogActivityTypes,
  listUserActivityLogCompanies,
  listUserActivityLogUsers,
  listUserActivityLogs,
} from "./UserAuditLog.requests";
import {
  getListParams,
  getUserAuditLogCompanyUserIds,
  getUserAuditLogDatePresetRange,
  getUserAuditLogFilterKey,
  getUserAuditLogUserOptions,
  USER_AUDIT_LOG_DATE_PRESETS,
  UserAuditLogDatePreset,
} from "./UserAuditLog.utils";

const UserAuditLog = () => {
  useScreen(UserAuditLogScreenDefinition);

  const componentColors = useComponentColors();
  const { download } = useDownloadFile();
  const { filters, setFilters } = useUserAuditLogFilters();
  const previousFiltersRef = useRef(filters);
  const [fetchDataParams, setFetchDataParams] = useState<
    FetchDataParamsWithToken<UserAuditLogOrderBy>
  >(defaultListUserActivityLogsParams);
  const [selectedActivityLog, setSelectedActivityLog] =
    useState<UserActivityLogTableRow | null>(null);
  const filterKey = useMemo(() => getUserAuditLogFilterKey(filters), [filters]);

  const columns = useMemo(
    () =>
      getUserAuditLogColumns({
        onDetailsClick: setSelectedActivityLog,
      }),
    [],
  );

  const listParams = useMemo(
    () => getListParams(fetchDataParams, filters, previousFiltersRef.current),
    [fetchDataParams, filters],
  );

  useEffect(() => {
    previousFiltersRef.current = filters;
  }, [filters]);

  const { data, isFetching } = useQuery(
    ["listUserActivityLogs", listParams],
    () => listUserActivityLogs(listParams),
  );

  const { data: activityTypesData, isFetching: isFetchingActivityTypes } =
    useQuery(
      ["listUserActivityLogActivityTypes"],
      listUserActivityLogActivityTypes,
    );

  const { data: companiesData, isFetching: isFetchingCompanies } = useQuery(
    ["listUserActivityLogCompanies"],
    listUserActivityLogCompanies,
  );

  const { data: usersData, isFetching: isFetchingUsers } = useQuery(
    ["listUserActivityLogUsers"],
    listUserActivityLogUsers,
  );

  useEffect(() => {
    const companyUserIds = getUserAuditLogCompanyUserIds({
      users: usersData?.users ?? [],
      companyId: filters.companyId,
      userIds: filters.userIds,
    });

    if (companyUserIds.join(",") !== filters.userIds.join(",")) {
      setFilters({
        ...filters,
        userIds: companyUserIds,
      });
    }
  }, [filters, setFilters, usersData?.users]);

  const activityTypeOptions = useMemo<Option<string>[]>(
    () =>
      (activityTypesData?.activityTypes ?? []).map((activityType) => ({
        value: activityType,
        label: activityType,
      })),
    [activityTypesData],
  );

  const selectedActivityTypeOptions = useMemo<Option<string>[]>(
    () =>
      filters.activityNames.map((activityType) => ({
        value: activityType,
        label: activityType,
      })),
    [filters.activityNames],
  );

  const companyOptions = useMemo<Option<number>[]>(
    () =>
      (companiesData?.companies ?? []).map((company) => ({
        value: company.companyId,
        label: company.displayName,
      })),
    [companiesData],
  );

  const selectedCompanyOption = useMemo(
    () =>
      filters.companyId
        ? companyOptions.find((company) => company.value === filters.companyId)
        : null,
    [companyOptions, filters.companyId],
  );

  const userOptions = useMemo<Option<number>[]>(
    () => getUserAuditLogUserOptions(usersData?.users ?? [], filters.companyId),
    [filters.companyId, usersData],
  );

  const selectedUserOptions = useMemo(
    () => userOptions.filter((user) => filters.userIds.includes(user.value)),
    [filters.userIds, userOptions],
  );

  const activeDatePreset = useMemo(
    () =>
      USER_AUDIT_LOG_DATE_PRESETS.find((preset) => {
        const range = getUserAuditLogDatePresetRange(preset.value);

        return (
          range.startDate === filters.startDate &&
          range.endDate === filters.endDate
        );
      })?.value,
    [filters.endDate, filters.startDate],
  );

  const hasActiveFilters = !isUserAuditLogFiltersEmpty(filters);

  const handleOnFetchDataChange = useCallback(
    (params: FetchDataParamsWithToken<UserAuditLogOrderBy>) => {
      setFetchDataParams(params);
    },
    [],
  );

  const handleDownloadClick = () => {
    download({
      url: Endpoints.UserAuditLog.Get.Export,
      fileName: "user_audit_log",
      fileExtension: "csv",
      params: hasActiveFilters
        ? buildUserAuditLogExportParams(filters)
        : undefined,
    });
  };

  const handleStartDateChange = (startDate: string) => {
    setFilters({
      ...filters,
      startDate,
      endDate:
        filters.endDate && filters.endDate < startDate
          ? startDate
          : filters.endDate,
    });
  };

  const handleEndDateChange = (endDate: string) => {
    setFilters({
      ...filters,
      startDate:
        filters.startDate && filters.startDate > endDate
          ? endDate
          : filters.startDate,
      endDate,
    });
  };

  const handleDatePresetClick = (preset: UserAuditLogDatePreset) => {
    const { startDate, endDate } = getUserAuditLogDatePresetRange(preset);

    setFilters({
      ...filters,
      startDate,
      endDate,
    });
  };

  const userActivityLogs = (data?.userActivityLogs ??
    []) as UserActivityLogTableRow[];

  return (
    <ViewContainer>
      <Flex alignItems="center" gap={3} mb={4}>
        <Search
          key={filters.search}
          currentValue={filters.search}
          placeholder="Search audit log"
          onSearch={(search) => {
            setFilters({
              ...filters,
              search: search.trim(),
            });
          }}
          mb={0}
        />

        <Flex grow={1} />

        <Button
          variant="outline-teal"
          leftIcon={<FiDownload />}
          onClick={handleDownloadClick}
        >
          {hasActiveFilters ? "Download Filtered" : "Download All"}
        </Button>
      </Flex>

      <Flex alignItems="flex-end" gap={3} mb={4} flexWrap="wrap">
        <Filter<string, unknown>
          label="Activity Type"
          placeholder="All activity types"
          isLoading={isFetchingActivityTypes}
          isClearable
          minW="240px"
          options={activityTypeOptions}
          selected={selectedActivityTypeOptions}
          onFilterValuesChange={(options) => {
            setFilters({
              ...filters,
              activityNames: options.map((option) => option.value),
            });
          }}
        />

        <Stack minW="280px">
          <Text
            {...FILTER_LABEL_TEXT_PROPS}
            color={componentColors.form.formLabelColor}
          >
            Date Range:
          </Text>
          <Flex gap={2} flexWrap="wrap">
            {USER_AUDIT_LOG_DATE_PRESETS.map((preset) => (
              <Button
                key={preset.value}
                size="sm"
                variant={
                  activeDatePreset === preset.value ? "primary-teal" : "outline"
                }
                onClick={() => handleDatePresetClick(preset.value)}
              >
                {preset.label}
              </Button>
            ))}
          </Flex>
        </Stack>

        <Stack minW="150px">
          <Text
            {...FILTER_LABEL_TEXT_PROPS}
            color={componentColors.form.formLabelColor}
          >
            Start Date:
          </Text>
          <DayPicker
            key={`start-date-${filters.startDate}`}
            initialValue={filters.startDate}
            onChange={handleStartDateChange}
          />
        </Stack>

        <Stack minW="150px">
          <Text
            {...FILTER_LABEL_TEXT_PROPS}
            color={componentColors.form.formLabelColor}
          >
            End Date:
          </Text>
          <DayPicker
            key={`end-date-${filters.endDate}`}
            initialValue={filters.endDate}
            onChange={handleEndDateChange}
          />
        </Stack>

        <Stack minW="300px">
          <Text
            {...FILTER_LABEL_TEXT_PROPS}
            color={componentColors.form.formLabelColor}
          >
            User:
          </Text>
          <Select<number, Option<number>, true>
            isMulti={true}
            isClearable
            isLoading={isFetchingUsers}
            placeholder="All users"
            options={userOptions}
            value={selectedUserOptions}
            onChange={(options) => {
              setFilters({
                ...filters,
                userIds: options.map((option) => option.value),
              });
            }}
          />
        </Stack>

        <Stack minW="260px">
          <Text
            {...FILTER_LABEL_TEXT_PROPS}
            color={componentColors.form.formLabelColor}
          >
            Company:
          </Text>
          <Select<number, Option<number>, false>
            isMulti={false}
            isClearable
            isLoading={isFetchingCompanies}
            placeholder="All companies"
            options={companyOptions}
            value={selectedCompanyOption ?? null}
            onChange={(option) => {
              const companyId = option?.value ?? null;

              setFilters({
                ...filters,
                companyId,
                userIds: getUserAuditLogCompanyUserIds({
                  users: usersData?.users ?? [],
                  companyId,
                  userIds: filters.userIds,
                }),
              });
            }}
          />
        </Stack>

        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              setFilters({
                search: "",
                activityNames: [],
                startDate: "",
                endDate: "",
                companyId: null,
                userIds: [],
              })
            }
          >
            Clear all
          </Button>
        )}
      </Flex>

      <Table<UserActivityLogTableRow>
        key={filterKey}
        variant="intentsifyNew"
        searchValue={filters.search}
        defaultSort={[{ id: "loggedAt", desc: true }]}
        isFetching={isFetching}
        columns={columns}
        data={userActivityLogs}
        onFetchDataChange={handleOnFetchDataChange}
        resetPaginationDependencies={[
          filters.search,
          filters.activityNames,
          filters.startDate,
          filters.endDate,
          filters.companyId,
          filters.userIds,
        ]}
        paginationType={PaginationType.TOKEN}
        nextPageToken={data?.nextPageToken}
      />

      <UserAuditLogDetailsDrawer
        activityLog={selectedActivityLog}
        isOpen={Boolean(selectedActivityLog)}
        onClose={() => setSelectedActivityLog(null)}
      />
    </ViewContainer>
  );
};

export { UserAuditLog };
